import { useState } from "react";
import { openWhatsApp } from "@/siteConfig";
import { Chip, LineInput, DualCTA, Eyebrow } from "@/components/bits";
import { useLang } from "@/i18n";

const STEPS = [
  {
    key: "asset_type",
    titleKey: "own.s1.title",
    options: ["opt.asset.beach", "opt.asset.farm", "opt.asset.hotel", "opt.asset.inst"],
  },
  {
    key: "value_band",
    titleKey: "own.s2.title",
    options: ["opt.band.1", "opt.band.2", "opt.band.3", "opt.band.4"],
  },
  {
    key: "location",
    titleKey: "own.s3.title",
    options: ["opt.loc.puri", "opt.loc.bbsr", "opt.loc.ctc", "opt.loc.other"],
  },
];

export const OwnersSection = ({ onBook }) => {
  const { t, tf } = useLang();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    asset_type: "",
    value_band: "",
    location: "",
    name: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const current = STEPS[step];
  const isContactStep = step === STEPS.length;
  const canContinue = current ? Boolean(form[current.key]) : Boolean(form.name.trim() && form.phone.trim());

  const summary = [
    t("wa.own.title"),
    `${t("wa.asset")}: ${t(form.asset_type)}`,
    `${t("wa.band")}: ${t(form.value_band)}`,
    `${t("wa.location")}: ${t(form.location)}`,
    `${t("wa.name")}: ${form.name}`,
    `${t("wa.phone")}: ${form.phone}`,
  ].join("\n");

  const submit = () => {
    openWhatsApp(summary);
    setSubmitted(true);
  };

  return (
    <section id="owners" data-testid="owners-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <Eyebrow>{t("own.eyebrow")}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.1]">
            {t("own.h1a")} <em className="italic text-[#B8894B]">{t("own.h1em")}</em>
            {t("own.h1b")}
          </h2>
          <p className="font-light text-base md:text-lg text-[#EDE7DA]/65 leading-relaxed mt-6">
            {t("own.body")}
          </p>
          <div className="border-l-2 border-[#B8894B] pl-5 mt-8">
            <p className="font-light text-sm text-[#EDE7DA]/55 leading-relaxed">
              {t("own.conf")}
            </p>
          </div>
        </div>

        <div className="md:col-span-7 border border-[#1A2A24] bg-[#131F1A] p-7 md:p-12">
          {submitted ? (
            <div data-testid="owner-mandate-success">
              <Eyebrow>{t("succ.eyebrow")}</Eyebrow>
              <h3 className="font-display text-2xl md:text-3xl text-[#EDE7DA] mt-4 tracking-tight">
                {tf("own.success.h", { name: form.name.split(" ")[0] })}
              </h3>
              <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-4">
                {t("own.success.body")}
              </p>
              <div className="mt-8">
                <DualCTA
                  prefix="owner-mandate"
                  waMessage={summary}
                  onBook={onBook}
                  waLabel={t("cta.waAgain")}
                  bookLabel={t("cta.bookCall")}
                />
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <span
                  data-testid="owner-step-indicator"
                  className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/50"
                >
                  {tf("own.stepTpl", { n: Math.min(step + 1, 4) })}
                </span>
                <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#B8894B]">
                  {t("own.stepTag")}
                </span>
              </div>
              <div className="h-px bg-[#1A2A24] mt-4">
                <div
                  className="h-px bg-[#B8894B] transition-[width] duration-500"
                  style={{ width: `${((step + 1) / 4) * 100}%` }}
                />
              </div>

              {!isContactStep ? (
                <div className="mt-10">
                  <h3 className="font-display text-2xl text-[#EDE7DA] tracking-tight">
                    {t(current.titleKey)}
                  </h3>
                  <div className="flex flex-wrap gap-3 mt-7">
                    {current.options.map((optKey) => (
                      <Chip
                        key={optKey}
                        testId={`owner-step-${step + 1}-option-${optKey.split(".").pop()}`}
                        active={form[current.key] === optKey}
                        onClick={() => setForm({ ...form, [current.key]: optKey })}
                      >
                        {t(optKey)}
                      </Chip>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-10">
                  <h3 className="font-display text-2xl text-[#EDE7DA] tracking-tight">
                    {t("own.s4.title")}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-8 mt-7">
                    <LineInput
                      label={t("own.nameLabel")}
                      testId="owner-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("ph.name")}
                    />
                    <LineInput
                      label={t("own.phoneLabel")}
                      testId="owner-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-12">
                <button
                  type="button"
                  data-testid="owner-step-back"
                  onClick={() => setStep(Math.max(0, step - 1))}
                  className={`font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#EDE7DA]/50 hover:text-[#B8894B] transition-colors duration-300 ${
                    step === 0 ? "invisible" : ""
                  }`}
                >
                  {t("cta.back")}
                </button>
                {!isContactStep ? (
                  <button
                    type="button"
                    data-testid="owner-step-continue"
                    disabled={!canContinue}
                    onClick={() => setStep(step + 1)}
                    className="font-mono-label text-[11px] tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-3 hover:bg-[#EDE7DA] transition-colors duration-300 disabled:opacity-30 disabled:hover:bg-[#B8894B]"
                  >
                    {t("cta.continue")}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="owner-mandate-submit"
                    disabled={!canContinue}
                    onClick={submit}
                    className="font-mono-label text-[11px] tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-3 hover:bg-[#EDE7DA] transition-colors duration-300 disabled:opacity-30 disabled:hover:bg-[#B8894B]"
                  >
                    {t("own.submit")}
                  </button>
                )}
              </div>
              <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40 mt-5 text-right">
                {t("cta.waNote")}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
